/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Table,
  Button,
  Tooltip,
  Popconfirm,
  Space,
} from 'antd';
import {
  DeleteOutlined,
  InfoOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  PageSection,
  messageError,
} from '@deltaohm/ant-components';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';

import { SOUND_SESSION_DELETE_MUTATION, WORKSPACE_SOUND_SESSIONS_QUERY, SOUND_SESSION_MULTI_DELETE_MUTATION } from '../../soundSessions/queries';

import { extractGraphQLErrors } from '../../utils/graphql';

import { ErrorList } from '../../generics';
import {
  computeDuration,
  formatLocale,
  formatLocaleWithTimezone,
  getLocale,
} from '../../utils/date';

import {
  SoundSessionInfoModal,
  SoundSessionTimePreviewModal,
  SoundSessionSamplePreviewModal,
  SoundSessionFilter,
} from '../../soundSessions/components';
import { AuthenticationContext } from '../../authentications';

const WorkspaceSoundSessionsTab = (props) => {
  const {
    tab,
    workspace,
  } = props;

  const { quota } = React.useContext(AuthenticationContext);

  const availableQuota = React.useMemo(() => {
    const {
      workspaceAvailableQuota,
    } = quota;
    return workspaceAvailableQuota;
  }, [quota]);

  const [searchObject, setSearchObject] = React.useState({
    search: null,
    from: null,
    to: null,
  });
  const [infoSoundSession, setInfoSoundSession] = React.useState(null);
  const [timePreviewSoundSession, setTimePreviewSoundSession] = React.useState(null);
  const [samplePreviewSoundSession, setSamplePreviewSoundSession] = React.useState(null);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const locale = getLocale(i18n.language);

  const {
    error,
    loading,
    data,
    refetch,
    fetchMore,
  } = useQuery(WORKSPACE_SOUND_SESSIONS_QUERY, {
    variables: {
      workspaceId: workspace.id,
      filter: searchObject,
      pageSize: 20,
    },
    fetchPolicy: 'network-only',
  });

  React.useEffect(() => refetch(), [tab]);

  const [
    soundSessionDeleteMutationAction,
    {
      loading: loadingDelete,
      error: errorDelete,
    },
  ] = useMutation(SOUND_SESSION_DELETE_MUTATION, {
    refetchQueries: () => {
      const result = [
        {
          query: WORKSPACE_SOUND_SESSIONS_QUERY,
          variables: {
            workspaceId: workspace.id,
            filter: searchObject,
            pageSize: 20,
          },
        },
      ];
      return result;
    },
    awaitRefetchQueries: true,
  });

  const [
    soundSessionMultiDeleteMutationAction,
    {
      loading: loadingMultiDelete,
      error: errorMultiDelete,
    },
  ] = useMutation(SOUND_SESSION_MULTI_DELETE_MUTATION, {
    refetchQueries: () => {
      const result = [
        {
          query: WORKSPACE_SOUND_SESSIONS_QUERY,
          variables: {
            workspaceId: workspace.id,
            filter: searchObject,
            pageSize: 20,
          },
        },
      ];
      return result;
    },
    awaitRefetchQueries: true,
  });

  React.useEffect(() => {
    if (error || errorDelete || errorMultiDelete) {
      const errors = extractGraphQLErrors(error || errorDelete || errorMultiDelete, t);
      messageError({
        content: <ErrorList errors={errors} />,
      });
    }
  }, [error, errorDelete, t]);

  const queryResult = React.useMemo(() => {
    if (!loading && !error) {
      const { viewer } = data;
      const { workspace: queryWorkspace } = viewer;
      const { soundSessions: soundSessionConnection } = queryWorkspace;
      const { pageInfo, edges } = soundSessionConnection;
      return {
        soundSessions: edges.map((e) => e.node),
        pageInfo,
      };
    }
    return {
      soundSessions: [],
      pageInfo: null,
    };
  }, [data, error, loading]);

  const handleLoadMore = () => {
    fetchMore({
      variables: {
        afterCursor: queryResult.pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const { edges: oldEdges } = previousResult.viewer.workspace.soundSessions;
        const { pageInfo: newPageInfo, edges: newEdges } = fetchMoreResult
          .viewer.workspace.soundSessions;
        return newEdges.length
          ? {
            ...previousResult,
            viewer: {
              ...previousResult.viewer,
              workspace: {
                ...previousResult.viewer.workspace,
                soundSessions: {
                  edges: [...oldEdges, ...newEdges],
                  pageInfo: newPageInfo,
                },
              },
            },
          }
          : previousResult;
      },
    });
  };

  const handleSearch = React.useCallback((newSearchObject) => {
    setSearchObject((old) => ({
      ...old,
      ...newSearchObject,
    }));
  }, [setSearchObject]);

  const columns = React.useMemo(() => ([
    {
      title: t('workspaces.soundSessionsTab.table.name'),
      dataIndex: 'name',
      fixed: 'left',
      render: (name) => (
        <div style={{ width: 200 }}>
          {name}
        </div>
      ),
    },
    {
      title: t('workspaces.soundSessionsTab.table.createdAt'),
      dataIndex: 'createdAt',
      render: (createdAt) => (
        <div style={{ width: 200 }}>
          {formatLocale(createdAt, 'PPpp', locale)}
        </div>
      ),
    },
    {
      title: t('workspaces.soundSessionsTab.table.recordDate'),
      dataIndex: 'recordDate',
      render: (recordDate, record) => (
        <div style={{ width: 200 }}>
          <Tooltip title={formatLocaleWithTimezone(recordDate, 'PPpp', locale, record.timezone, true)}>
            {formatLocale(recordDate, 'PPpp', locale)}
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('workspaces.soundSessionsTab.table.source'),
      dataIndex: 'source',
    },
    {
      title: t('workspaces.soundSessionsTab.table.reference'),
      dataIndex: 'reference',
    },
    {
      title: t('workspaces.soundSessionsTab.table.duration'),
      dataIndex: 'minTimestamp',
      render: (minTimestamp, record) => {
        if (record.minTimestamp && record.maxTimestamp) {
          const diff = record.maxTimestamp - record.minTimestamp;
          if (!diff) {
            return computeDuration(record.recordDate, record.maxTimestamp);
          }
          return computeDuration(record.minTimestamp, record.maxTimestamp);
        }
        if (record.minSample != null && record.maxSample != null) {
          return `${record.maxSample - record.minSample} ${t('common.samples')}`;
        }
        return null;
      },
    },
    {
      title: t('workspaces.soundSessionsTab.table.weight'),
      dataIndex: 'weight',
      render: (weight) => {
        if (availableQuota === null) {
          return weight;
        }
        return `${(weight / availableQuota).toFixed(2)}%`;
      },
    },
    {
      title: t('common.actions'),
      dataIndex: 'id',
      fixed: 'right',
      render: (id, record) => {
        const result = (
          <Space size="small">
            <Tooltip title={t('common.detail')}>
              <Button shape="circle" size="small" type="primary" onClick={() => navigate(`/workspaces/${workspace.id}/soundSessions/${id}`)}>
                <RightOutlined />
              </Button>
            </Tooltip>
            <Tooltip title={t('common.info')}>
              <Button shape="circle" size="small" type="primary" onClick={() => setInfoSoundSession(record)}>
                <InfoOutlined />
              </Button>
            </Tooltip>
            {record.timeMeasurePreview && (
              <Tooltip title={t('workspaces.soundSessionsTab.table.timePreview')}>
                <Button shape="circle" size="small" type="primary" onClick={() => setTimePreviewSoundSession(record)}>
                  <SearchOutlined />
                </Button>
              </Tooltip>
            )}
            {record.sampleMeasurePreview && (
              <Tooltip title={t('workspaces.soundSessionsTab.table.samplePreview')}>
                <Button shape="circle" size="small" type="primary" onClick={() => setSamplePreviewSoundSession(record)}>
                  <SearchOutlined />
                </Button>
              </Tooltip>
            )}
            <Popconfirm
              title={t('common.sureToDelete')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={() => soundSessionDeleteMutationAction({
                variables: {
                  input: {
                    workspaceId: workspace.id,
                    id,
                  },
                },
              })}
            >
              <Tooltip title={t('common.delete')}>
                <Button shape="circle" size="small" type="ghost">
                  <DeleteOutlined />
                </Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        );
        return result;
      },
    },
  ]), [t, availableQuota]);

  const tableFooter = React.useMemo(() => {
    if (queryResult.pageInfo && queryResult.pageInfo.hasNextPage) {
      return () => (
        <Button type="primary" block onClick={handleLoadMore}>
          {t('common.loadMore')}
        </Button>
      );
    }
    return null;
  }, [queryResult, handleLoadMore]);

  return (
    <>
      <PageSection
        title={t('workspaces.soundSessionsTab.title')}
        subtitle={t('workspaces.soundSessionsTab.subtitle')}
      >
        <Row>
          <Col xs={24}>
            <SoundSessionFilter
              onSearch={handleSearch}
            />
          </Col>
        </Row>
        <Row justify="end" style={{ marginTop: 8, marginBottom: 8 }}>
          <Popconfirm
            title={t('common.sureToDeleteInView')}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            onConfirm={() => soundSessionMultiDeleteMutationAction({
              variables: {
                input: {
                  workspaceId: workspace.id,
                  ids: queryResult.soundSessions.map((ss) => ss.id),
                },
              },
            })}
          >
            <Button>
              {t('common.deleteInView')}
            </Button>
          </Popconfirm>
        </Row>
        <Row>
          <Col xs={24}>
            <Table
              bordered
              size="small"
              scroll={{ x: true }}
              columns={columns}
              rowKey={(record) => record.id}
              dataSource={queryResult.soundSessions}
              loading={loading || loadingDelete || loadingMultiDelete}
              pagination={false}
              footer={tableFooter}
            />
          </Col>
        </Row>
      </PageSection>
      {infoSoundSession && (
        <SoundSessionInfoModal
          workspaceId={workspace.id}
          soundSession={infoSoundSession}
          isModalOpen={infoSoundSession !== null}
          onClose={() => setInfoSoundSession(null)}
        />
      )}
      {timePreviewSoundSession && (
        <SoundSessionTimePreviewModal
          workspaceId={workspace.id}
          soundSession={timePreviewSoundSession}
          onClose={() => setTimePreviewSoundSession(null)}
          isModalOpen={timePreviewSoundSession !== null}
        />
      )}
      {samplePreviewSoundSession && (
        <SoundSessionSamplePreviewModal
          workspaceId={workspace.id}
          soundSession={samplePreviewSoundSession}
          onClose={() => setSamplePreviewSoundSession(null)}
          isModalOpen={samplePreviewSoundSession !== null}
        />
      )}
    </>
  );
};

const propTypes = {
  workspace: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  tab: PropTypes.string.isRequired,
};

WorkspaceSoundSessionsTab.propTypes = propTypes;

export default WorkspaceSoundSessionsTab;
